import i18n from "@/i18n";
import StringUtils from "@/utils/StringUtils";
import moment from "moment";
import {
  APOfferVacancyMeta,
  VacancyContractDepositFormType,
  VacancyContractFormType,
  VacancyContractHouseRulesFormType,
  VacancyContractSummaryFormFields,
  VacancyContractSummaryFormType,
} from "../../model/VacancyApplicant.interface";
import { VariableItem } from "@/modules/ckeditor/TextEditor";
import ArrayUtils from "@/utils/ArrayUtils";
import {
  BANK_VARIABLE,
  OBJECT_VARIABLE,
} from "@/modules/abstract-ui/forms/document-editor/BFDocument.service";
import { APOffer } from "@/apps/tatar/activityApp/views/details/offer-feature/APOffer.Interface";
import { APActivity } from "@/apps/tatar/activityApp/ActivityInterfaces";
import CacheService from "@/services/CacheService";
import { AssetTypes } from "@/model/AssetTypes";
import { TemplateAsset } from "@/modules/template-module/TemplateInterfaces";
import _ from "lodash";
import TextEditorUtils from "@/modules/ckeditor/TextEditorUtils";
import { getLetterFooter } from "@/modules/abstract-ui/forms/document-editor/BFLetterFooter";
import { HTTP } from "@/utils/Http";
import Log from "@/debug/Log";
import DataBusDefaults from "@/services/DataBusDefaults";
import LanguageService from "@/services/LanguageService";
import { diffChars } from "diff";
import { User } from "@sentry/react";
import { VacancyContractSendPayload } from "../../model/VacancyContract.interface";
import { getVacancyContractHeader } from "../../vacancy-applicant-contract/components/form-parts/contract/VacancyContractHeader";
import VacancyOfferUtils from "./VacancyOfferUtils";
import CommentsService from "@/modules/comments-module/CommentsService";
import { ActivityApplicationConstants } from "@/apps/tatar/activityApp/ActivityHooks";

class VacancyContractUtilsClass {
  submitAndSendContract = async (params: {
    formStates: {
      contractForm: VacancyContractFormType;
      depositForm: VacancyContractDepositFormType;
      houseRulesForm: VacancyContractHouseRulesFormType;
      summaryForm: VacancyContractSummaryFormType;
    };
    constants: ActivityApplicationConstants;
    activity: APActivity;
    offer: APOffer;
    replacements: any;
    conditions: { [key: string]: boolean };
    infoMailTemplate: string;
    brandId: string;
    onBehalfOf?: string;
    digitalSignerId: string;
  }) => {
    try {
      const {
        formStates,
        constants,
        activity,
        offer,
        replacements,
        conditions,
        infoMailTemplate,
        brandId,
        onBehalfOf,
        digitalSignerId
      } = params;
      // 1. build up replacements
      const templateReplacements = this.getReplacementsWithFormData(
        replacements,
        formStates.contractForm,
        formStates.depositForm
      );

      const formatters = this.getReplacementFormaters();
      // 2. get Footer and Header template
      const letterTemplateId = formStates.contractForm.letterTemplateId;

      const letterHeaderTemplate = (await CacheService.getData({
        assetType: AssetTypes.Template,
        id: letterTemplateId,
        oType: "asset",
        ignoreDelay: true,
      })) as TemplateAsset;

      // 3. chain the formTemplates
      //     Contract
      //     Deposit
      //     Houserules
      const templateContentHtml = TextEditorUtils.replaceVariables(
        this.getChainedTemplate(formStates),
        templateReplacements,
        {
          conditions,
          formatFunctions: formatters,
        }
      );

      // 4. prepare header and footers
      const header = TextEditorUtils.replaceVariables(
        getVacancyContractHeader(letterHeaderTemplate.data.data),
        templateReplacements,
        {
          conditions,
          formatFunctions: formatters,
        }
      );
      const footer = TextEditorUtils.replaceVariables(
        getLetterFooter(letterHeaderTemplate.data.data, false),
        templateReplacements,
        {
          conditions,
          formatFunctions: formatters,
        }
      );

      // 5. get User assigned to activity
      const assignedUser = await this.getAssigendUser(digitalSignerId);
      
      // 6. get template diffs
      const contractDiff = diffChars(
        formStates.contractForm.templateContent,
        formStates.summaryForm.contractContent
      );
      const depositDiff = diffChars(
        formStates.depositForm.templateContent,
        formStates.summaryForm.depositContent
      );
      const houseRulesDiff = diffChars(
        formStates.houseRulesForm.templateContent,
        formStates.summaryForm.houseRulesContent
      );

      // 7. gather form data
      const contractData = {
        ..._.omit(
          formStates.contractForm,
          "templatePrefix",
          "templateContent",
          "templateSuffix"
        ),
        ..._.omit(
          formStates.depositForm,
          "templatePrefix",
          "templateContent",
          "templateSuffix"
        ),
        ..._.omit(
          formStates.houseRulesForm,
          "templatePrefix",
          "templateContent",
          "templateSuffix"
        ),
        houseRulesDiff: houseRulesDiff.length > 1 ? houseRulesDiff : undefined,
        contractDiff: contractDiff.length > 1 ? contractDiff : undefined,
        depositDiff: depositDiff.length > 1 ? depositDiff : undefined,
        landLordSignerId: assignedUser._id,
      };

      const signDigital = formStates.summaryForm.pdfCreation === "boldsign";

      const tenantsCount = formStates.contractForm.tenants.length;
      const downloadFileName = `${formStates.contractForm.objectAddress}_${replacements.objectCity}`;

      // 8. build up payload
      const lang = LanguageService.getMandatorDefaultLanguage();
      const contractSigners = formStates.contractForm.tenants.map((t, i) => {
        return {
          // TODO SJ @WurstMitBrot Make this  configurable by environment
          // verifyId: true,
          email: t.email,
          name: `${t.firstName} ${t.lastName}`,
          index: i + 1,
          locale: lang.toUpperCase(),
        };
      });

      const sendContractBody: VacancyContractSendPayload = {
        content: {
          header: header,
          footer: footer,
          template: `<font face="Calibri, sans-serif">
      ${templateContentHtml}</font>`,
        },
        contractData,
        documentInfo: {
          signDigital: signDigital,
          locale: lang.toUpperCase(),
          signers: [
            ...contractSigners,
            {
              email: assignedUser.email,
              name: assignedUser.displayname || assignedUser.email,
              index: tenantsCount + 1,
              locale:
                lang.charAt(0).toUpperCase() + lang.slice(1).toLowerCase(),
            },
          ],
          downloadFileName: `Mietvertrag ${downloadFileName}`.replace(
            /[^a-zA-Z0-9_-]/g,
            "_"
          ),
          onBehalfOf: onBehalfOf || assignedUser.email,
          brandId: brandId,
          title: `${i18n.t(
            "apTemplate:Vacancy.Applicant.Contract.Label.SignTitle",
            "Mietvertrag"
          )} ${formStates.contractForm.objectAddress}`,
        },
      };

      // 9. make request for sending contract
      const response = await HTTP.post({
        url: `/vacancy/offer/${offer._id}/sendContract`,
        target: "EMPTY",
        bodyParams: sendContractBody,
      });

      DataBusDefaults.toast({
        text: i18n.t(
          "apTemplate:Vacancy.Applicant.Contract.Label.SaveAndSendSuccess",
          "Der Vertrag wurde erfolgreich gespeichert."
        ),
        type: "success",
      });

      if (signDigital) {
        const tenants = formStates.contractForm.tenants || [];
        // Send Info Mail
        for (const newTenant of tenants) {
          const tenantSalutationText =
            VacancyOfferUtils.getSalutationText(newTenant);

          const infoMailTemplateReplaced = TextEditorUtils.replaceVariables(
            infoMailTemplate,
            { ...templateReplacements, tenantSalutationText },
            {
              conditions,
              formatFunctions: formatters,
            }
          );

          const emailUser = {
            address: newTenant.email,
            name: `${newTenant.firstName} ${newTenant.lastName}`,
          };

          // now this await actually pauses the loop until sendMail resolves
          await CommentsService.sendMail({
            assetType: constants.assetType,
            assetId: activity._id,
            message: infoMailTemplateReplaced,
            subject: i18n.t(
              "apTemplate:Vacancy.Applicant.ContractInfo.Subject",
              "Zusage Ihrer Wohnungsbewerbung – Digitaler Mietvertrag zur Unterschrift"
            ),
            recipients: [emailUser],
          });
        }
      }
      
      return response;
    } catch (error) {
      DataBusDefaults.toast({
        text: i18n.t(
          "apTemplate:Vacancy.Applicant.Contract.Label.SaveAndSendError",
          "Beim Speichern des Vertrags ist ein Fehler aufgetreten."
        ),
        type: "error",
      });
      Log.error(error);
    }
  };

  changeDigitalContractSigner = async (
    offer: APOffer,
    newSignerId: string,
    onBehalfOf: string,
    reason: string
  ) => {
    try {
      const offerMeta = offer.data.meta as APOfferVacancyMeta;
      const oldSignerId = offerMeta.contractData.landLordSignerId;
      if (!oldSignerId) {
        throw new Error("No old signer ID found");
      }

      const oldUser = await this.getAssigendUser(oldSignerId);
      const user = await this.getAssigendUser(newSignerId);
      
      const newSigner = {
        newSignerName: user.displayname,
        newSignerEmail: user.email,
        oldSignerEmail: oldUser.email,
        reason: reason,
        order: offerMeta.contractData.tenants.length + 1,
        onBehalfOf: onBehalfOf,
      };

      const contractDocumentId = offerMeta.contracts?.[0]?.documentId;
      if (!contractDocumentId) {
        throw new Error("No contract document ID found");
      }

      await HTTP.post({
        url: `/vacancy/contract/${contractDocumentId}/changeRecipient`,
        target: "EMPTY",
        bodyParams: newSigner,
      });

      const updatedOffer = await HTTP.post({
        url: `/vacancy/${offer._id}/updateOffer`,
        target: "EMPTY",
        bodyParams: {
          meta: {
            ...offerMeta,
            contractData: {
              ...offerMeta.contractData,
              landLordSignerId: user._id,
            },
          },
        },
      });

      CacheService.updateDataInCaches(updatedOffer._id, updatedOffer);

      DataBusDefaults.toast({
        type: "success",
        text: i18n.t(
          "apTemplate:Vacancy.Applicant.ContractDetails.Label.OfferAccepted.ChangeSignerSuccess",
          "Der Unterzeichner wurde erfolgreich geändert."
        ),
      });
      return true;
    } catch (error) {
      console.error("Error changing signer:", error);
      DataBusDefaults.toast({
        type: "error",
        text: i18n.t(
          "apTemplate:Vacancy.Applicant.ContractDetails.Label.OfferAccepted.ChangeSignerError",
          "Fehler beim Ändern des Unterzeichners."
        ),
      });
      return false;
    }
  };

  getAssigendUser = async (userId: string): Promise<User> => {
    try {
      const assignedUserRes = await CacheService.getData({
        id: userId,
        oType: "user",
      });

      return assignedUserRes as User;
    } catch (error) {
      throw new Error(`Error fetching user ${userId}`);
    }
  };

  getChainedTemplate = (formStates: {
    contractForm: VacancyContractFormType;
    depositForm: VacancyContractDepositFormType;
    houseRulesForm: VacancyContractHouseRulesFormType;
    summaryForm: VacancyContractSummaryFormType;
  }) => {
    const pageBreak = '<div style="page-break-before: always;"></div>';
    const contractTemplate = `<div class="address-part">${formStates.contractForm.templatePrefix}</div><div class="address-part">${formStates.summaryForm.contractContent}</div><div class="signature-part">${formStates.contractForm.templateSuffix}</div>`;
    const depositTemplate = `<div class="deposit-prefix-part">${formStates.depositForm.templatePrefix}</div><div class="deposit-part">${formStates.summaryForm.depositContent}</div><div class="signature-part">${formStates.depositForm.templateSuffix}</div>`;
    const houseRulesTemplate = `<div class="houserules-prefix-part">${formStates.houseRulesForm.templatePrefix}</div><div class="deposit-part">${formStates.summaryForm.houseRulesContent}</div><div class="signature-part">${formStates.houseRulesForm.templateSuffix}</div>`;
    return `${contractTemplate}${pageBreak}${depositTemplate}${pageBreak}${houseRulesTemplate}`;
  };

  calculateFloorDisplayName = (floor: number) => {
    if (floor > 0) {
      return `${floor}. ${i18n.t(
        "apTemplate:Vacancy.Applicant.Contract.Floor.UpperFloor",
        "Obergeschoss"
      )}`;
    }
    if (floor < 0) {
      return `${floor}. ${i18n.t(
        "apTemplate:Vacancy.Applicant.Contract.Floor.LowerFloor",
        "Untergeschoss"
      )}`;
    }

    return i18n.t(
      "apTemplate:Vacancy.Applicant.Contract.Floor.FirstFloor",
      "Erdgeschoss"
    );
  };

  getReplacementsWithFormData = (
    replacements: { [key: string]: string | number },
    contractForm?: VacancyContractFormType,
    depositForm?: VacancyContractDepositFormType
  ) => {
    const tenants = this.getTenantReplacements(contractForm);

    return {
      ...replacements,
      ...tenants,
      ..._.omit(
        contractForm,
        "templatePrefix",
        "templateContent",
        "templateSuffix",
        "tenants"
      ),
      ..._.omit(
        depositForm,
        "templatePrefix",
        "templateContent",
        "templateSuffix"
      ),
    };
  };

  getTenantReplacements = (contractForm?: VacancyContractFormType) => {
    const tenants = contractForm?.tenants?.reduce((acc, tenant, index) => {
      return {
        ...acc,
        [`applicantFirstName-${index}`]: tenant.firstName,
        [`applicantLastName-${index}`]: tenant.lastName,
        [`applicantStreet-${index}`]: tenant.street,
        [`applicantStreetNumber-${index}`]: tenant.streetNumber,
        [`applicantZip-${index}`]: tenant.zipCode,
        [`applicantCity-${index}`]: tenant.city,
      };
    }, {});
    return tenants || {};
  };

  getReplacementFormaters = () => {
    const formatCurrencyFunc = (value) => {
      return StringUtils.formatCurrency(value || 0, false, undefined, "EUR");
    };

    return {
      moveInDate: (date: Date) =>
        moment(date).format(i18n.t("Formats.dateFormat", "DD.MM.YYYY")),
      depositDate: (date: Date) =>
        moment(date).format(i18n.t("Formats.dateFormat", "DD.MM.YYYY")),
      depositSum: formatCurrencyFunc,
      rentalUnitRentBase: formatCurrencyFunc,
      rentalUnitAdditionalCosts: formatCurrencyFunc,
      rentalUnitRentTotal: formatCurrencyFunc,
    };
  };

  getContractDigitalSignOptions = () => {
    return [
      {
        value: "boldsign",
        label: i18n.t(
          "apTemplate:Vacancy.Applicant.Contract.Label.DigitalSignBoldSign",
          "Digitale Unterschrift"
        ),
      },
      {
        value: "manual",
        label: i18n.t(
          "apTemplate:Vacancy.Applicant.Contract.Label.DigitalSignManual",
          "Manuelle Unterschrift"
        ),
      },
    ];
  };

  getContractSummaryOptions = (): {
    value: VacancyContractSummaryFormFields;
    label: string;
  }[] => {
    return [
      {
        value: "contractContent",
        label: i18n.t(
          "apTemplate:Vacancy.Applicant.Contract.Summary.Contract",
          "1. Mietvertrag"
        ),
      },
      {
        value: "depositContent",
        label: i18n.t(
          "apTemplate:Vacancy.Applicant.Contract.Summary.Deposit",
          "2. Kaution"
        ),
      },

      {
        value: "houseRulesContent",
        label: i18n.t(
          "apTemplate:Vacancy.Applicant.Contract.Summary.Hausordnung",
          "3. Hausordnung"
        ),
      },
    ];
  };

  getEditorVariableList = (tenantCount: number, search: string) => {
    if (!search) {
      return CONTRACT_VARIABLES(tenantCount);
    }
    return ArrayUtils.fuzzyFilter(
      search,
      CONTRACT_VARIABLES(tenantCount, search),
      {
        keys: ["name"],
      }
    ).map((e) => e.item);
  };
}

const CONTRACT_VARIABLES: (
  tenantCount: number,
  search?: string
) => VariableItem[] = (tenantCount: number, search?: string) => {
  const tenantVariables = Array.from({ length: tenantCount }, (_, index) => {
    return [
      {
        id: `applicantFirstName-${index}`,
        name: `${i18n.t(
          "apTemplate:Vacancy.Applicant.Contract.Variable.ApplicantFirstName",
          "Mieter Vorname"
        )} ${index + 1}`,
      },
      {
        id: `applicantLastName-${index}`,
        name: `${i18n.t(
          "apTemplate:Vacancy.Applicant.Contract.Variable.ApplicantLastName",
          "Mieter Nachname"
        )} ${index + 1}`,
      },
      {
        id: `applicantStreet-${index}`,
        name: `${i18n.t(
          "apTemplate:Vacancy.Applicant.Contract.Variable.ApplicantStreet",
          "Mieter Straße"
        )} ${index + 1}`,
      },
      {
        id: `applicantStreetNumber-${index}`,
        name: `${i18n.t(
          "apTemplate:Vacancy.Applicant.Contract.Variable.ApplicantStreetNumber",
          "Mieter Hausnummer"
        )} ${index + 1}`,
      },
      {
        id: `applicantZip-${index}`,
        name: `${i18n.t(
          "apTemplate:Vacancy.Applicant.Contract.Variable.ApplicantZip",
          "Mieter PLZ"
        )} ${index + 1}`,
      },
      {
        id: `ApplicantCity-${index}`,
        name: `${i18n.t(
          "apTemplate:Vacancy.Applicant.Contract.Variable.ApplicantCity",
          "Mieter Stadt"
        )} ${index + 1}`,
      },
    ];
  });

  const flattenedTenantVariables = _.flatten(tenantVariables);
  return [
    ...BANK_VARIABLE(),
    ...OBJECT_VARIABLE(),
    ...flattenedTenantVariables,
    {
      id: "bankUsageReference",
      name: i18n.t(
        "apTemplate:Vacancy.Applicant.Contract.Variable.BankUsageReference",
        "Miete Verwendungszweck"
      ),
    },
    {
      id: "entityDisplayName",
      preventEdit: true,
      name: i18n.t(
        "apTemplate:Vacancy.Applicant.Contract.Variable.EntityDisplayName",
        "Gesellschaft"
      ),
    },
    {
      id: "rentalUnitArea",
      name: i18n.t(
        "apTemplate:Vacancy.Applicant.Contract.Variable.RentalUnitArea",
        "Mietfläche"
      ),
    },
    {
      id: "rentalUnitFloor",
      name: i18n.t(
        "apTemplate:Vacancy.Applicant.Contract.Variable.Floor",
        "Etage"
      ),
    },
    {
      id: "rentalUnitFloorDisplayName",
      name: i18n.t(
        "apTemplate:Vacancy.Applicant.Contract.Variable.RentalUnitFloorDisplayName",
        "Etage Bezeichnung"
      ),
    },

    {
      id: "moveInDate",
      name: i18n.t(
        "apTemplate:Vacancy.Applicant.Contract.Variable.MoveInDate",
        "Einzugsdatum"
      ),
    },
    {
      id: "rentalUnitRooms",
      name: i18n.t(
        "apTemplate:Vacancy.Applicant.Contract.Variable.RentalUnitRooms",
        "Anzahl Räume"
      ),
    },
    {
      id: "rentalUnitKitchen",
      name: i18n.t(
        "apTemplate:Vacancy.Applicant.Contract.Variable.RentalUnitKitchen",
        "Anzahl Küchen"
      ),
    },
    {
      id: "rentalUnitBathrooms",
      name: i18n.t(
        "apTemplate:Vacancy.Applicant.Contract.Variable.RentalUnitBathrooms",
        "Anzahl Bäder"
      ),
    },
    {
      id: "rentalUnitHallway",
      name: i18n.t(
        "apTemplate:Vacancy.Applicant.Contract.Variable.RentalUnitHallway",
        "Anzahl Flure"
      ),
    },
    {
      id: "rentalUnitRentBase",
      name: i18n.t(
        "apTemplate:Vacancy.Applicant.Contract.Variable.RentalUnitRentBase",
        "Grundmiete"
      ),
    },
    {
      id: "rentalUnitAdditionalCosts",
      name: i18n.t(
        "apTemplate:Vacancy.Applicant.Contract.Variable.RentalUnitAdditionalCosts",
        "Nebenkosten"
      ),
    },
    {
      id: "rentalUnitRentTotal",
      name: i18n.t(
        "apTemplate:Vacancy.Applicant.Contract.Variable.RentalUnitRentTotal",
        "Mietkosten gesamt"
      ),
    },
    {
      id: "bankUsageReferenceDeposit",
      name: i18n.t(
        "apTemplate:Vacancy.Applicant.Contract.Variable.BankUsageReferenceDeposit",
        "Kaution Verwendungszweck"
      ),
    },
    {
      id: "depositSum",
      name: i18n.t(
        "apTemplate:Vacancy.Applicant.Contract.Variable.DepositSum",
        "Kaution Summe"
      ),
    },
    {
      id: "depositDate",
      name: i18n.t(
        "apTemplate:Vacancy.Applicant.Contract.Variable.DepositDate",
        "Kaution Datum"
      ),
    },
  ];
};

const VacancyContractUtils = new VacancyContractUtilsClass();
export default VacancyContractUtils;
